import React, { useState, useMemo, useEffect } from 'react';
import { SubmissionData, Campaign } from '../../../types';
import { Download, Settings, X, Filter, GripVertical } from 'lucide-react';
import { apiClient } from '../../../services/apiClient';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { getToken } from '../../../utils/auth';
import type { ComputedMasterItem } from '../../../utils/masterDataCalculations';

interface Props {
  submissions: SubmissionData[];
  campaigns: Campaign[];
  currencyCode?: string;
}

interface FlatItem {
  id: string;
  date: string;
  brand: string;
  campaignId: string;
  campaignName: string;
  itemName: string;
  created: string;
  startDate: string;
  endDate: string;
  daysRun: string;
  daysLeft: string;
  duration: string;
  lifecycle: string;
  health: string;
  originalPrice: string;
  discountAmount: string;
  newPrice: string;
  status: string;
  discountType: string;
  discountValue: string;
  discount: string;
  fundingType: string;
  funding: string;
  sterlingShare: string;
  cofundedShare: string;
  platforms: string[];
}

interface ColumnConfig {
  key: keyof FlatItem;
  label: string;
  visible: boolean;
}

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'id', label: 'ID', visible: true },
  { key: 'date', label: 'Date', visible: true },
  { key: 'brand', label: 'Brand', visible: true },
  { key: 'campaignName', label: 'Campaign Name', visible: true },
  { key: 'itemName', label: 'Item Name', visible: true },
  { key: 'created', label: 'Created', visible: true },
  { key: 'startDate', label: 'Start Date', visible: true },
  { key: 'endDate', label: 'End Date', visible: true },
  { key: 'daysRun', label: 'Days Run', visible: true },
  { key: 'daysLeft', label: 'Days Left', visible: true },
  { key: 'duration', label: 'Duration (Days)', visible: true },
  { key: 'lifecycle', label: 'Lifecycle', visible: true },
  { key: 'health', label: 'Health', visible: true },
  { key: 'originalPrice', label: 'Original Price', visible: true },
  { key: 'discountAmount', label: 'Discount Amount', visible: true },
  { key: 'newPrice', label: 'New Price', visible: true },
  { key: 'status', label: 'Status', visible: true },
  { key: 'discountType', label: 'Discount Type', visible: true },
  { key: 'discountValue', label: 'Discount Value', visible: true },
  { key: 'discount', label: 'Discount', visible: true },
  { key: 'fundingType', label: 'Funding Type', visible: true },
  { key: 'funding', label: 'Funding', visible: true },
  { key: 'sterlingShare', label: 'Sterling Share', visible: true },
  { key: 'cofundedShare', label: 'Co-Funded Share', visible: true },
  { key: 'platforms', label: 'Participating Aggregators', visible: true },
];

// Fixed export columns - always export these regardless of user customization
const EXPORT_COLUMNS: (keyof FlatItem)[] = [
  'id',
  'date',
  'brand',
  'campaignName',
  'itemName',
  'created',
  'startDate',
  'endDate',
  'daysRun',
  'daysLeft',
  'duration',
  'lifecycle',
  'health',
  'originalPrice',
  'discountAmount',
  'newPrice',
  'status',
  'discountType',
  'discountValue',
  'discount',
  'fundingType',
  'funding',
  'sterlingShare',
  'cofundedShare',
  'platforms',
];

const mapComputedItemToFlat = (
  item: ComputedMasterItem,
  campaignNameById: Map<string, string>,
  campaignIdByName: Map<string, string>
): FlatItem => {
  const toNumberString = (value?: number | null, digits = 2) => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value.toFixed(digits);
    }
    return digits === 1 ? '0.0' : '0.00';
  };

  const normalizedDiscountType = item.discountType === 'Amount' ? 'Flat' : item.discountType || '';
  const normalizedDiscountLabel = normalizedDiscountType === 'Flat' && typeof item.discountValue === 'number'
    ? `${item.discountValue.toFixed(2)} off`
    : (item.discountLabel || '');
  const fundingTypeLabel = item.fundingType || 'None';
  const fundingChipLabel = item.fundingLabel
    ? item.fundingLabel.replace('Co-Funder', 'Co-Funded').replace(' of discount', '')
    : fundingTypeLabel;
  const platformList = Array.isArray(item.platforms) ? item.platforms.filter(Boolean) : [];
  const normalizeCampaignId = (value: unknown) => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return value.toString();
    return '';
  };

  // Format ID - handle both numeric IDs and UUID/string IDs
  let formattedId = 'SUB-0-0000';
  const rawId = item.id || '';
  if (rawId) {
    // Check if ID starts with SUB- already
    if (rawId.startsWith('SUB-')) {
      formattedId = rawId;
    } else {
      // Try to parse as number first
      const idNum = Number(rawId);
      if (!Number.isNaN(idNum) && idNum > 0) {
        const block = Math.floor(idNum / 10000);
        const last4 = idNum.toString().slice(-4).padStart(4, '0');
        formattedId = `SUB-${block}-${last4}`;
      } else {
        // Use last 8 characters of UUID/string as ID
        const shortId = rawId.replace(/-/g, '').slice(-8).toUpperCase();
        formattedId = `SUB-${shortId.slice(0, 4)}-${shortId.slice(4, 8) || '0000'}`;
      }
    }
  }
  const rawCampaignId = normalizeCampaignId((item as any).campaignId ?? (item as any).campaign_id ?? (item as any).campaignID);
  const rawCampaignName = item.campaign || (item as any).campaignName || (item as any).campaign_name || '';
  const resolvedCampaignName = (rawCampaignId ? campaignNameById.get(rawCampaignId) : '') || rawCampaignName || '—';
  const resolvedCampaignId = rawCampaignId || (rawCampaignName ? campaignIdByName.get(rawCampaignName) : '') || '';
  const createdLabel = item.campaign || item.created || '';

  return {
    id: formattedId,
    date: item.date ? new Date(item.date).toLocaleDateString('en-US') : '',
    brand: item.brand || 'Unknown',
    campaignId: resolvedCampaignId,
    campaignName: resolvedCampaignName,
    itemName: item.itemName || 'Unnamed Item',
    created: createdLabel,
    startDate: item.startDate || '',
    endDate: item.endDate || '',
    daysRun: (item.daysRun ?? 0).toString(),
    daysLeft: (item.daysLeft ?? 0).toString(),
    duration: `${item.durationDays ?? 0}d`,
    lifecycle: item.lifecycle || '',
    health: item.health || '',
    originalPrice: toNumberString(item.originalPriceQar, 2),
    discountAmount: toNumberString(item.discountAmountQar, 2),
    newPrice: toNumberString(item.newPriceQar, 2),
    status: item.status || 'Pending',
    discountType: normalizedDiscountType,
    discountValue: item.discountValue != null ? item.discountValue.toString() : '',
    discount: normalizedDiscountLabel,
    fundingType: fundingTypeLabel,
    funding: item.fundingLabel || fundingChipLabel,
    sterlingShare: toNumberString(item.sterlingShareQar, 1),
    cofundedShare: toNumberString(item.coFundedShareQar, 1),
    platforms: platformList,
  };
};

export const TableView: React.FC<Props> = ({ submissions: _submissions, campaigns: _campaigns, currencyCode }) => {
  const currency = currencyCode || 'QAR';
  const [columns, setColumns] = useState<ColumnConfig[]>(DEFAULT_COLUMNS);
  const [tempColumns, setTempColumns] = useState<ColumnConfig[]>(DEFAULT_COLUMNS);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState<Set<string>>(new Set(EXPORT_COLUMNS));
  const [isLoading, setIsLoading] = useState(true);
  const [masterItems, setMasterItems] = useState<ComputedMasterItem[]>([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterLifecycle, setFilterLifecycle] = useState('');
  const [filterFundingType, setFilterFundingType] = useState('');
  const [filterDiscountType, setFilterDiscountType] = useState('');
  const [filterHealth, setFilterHealth] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterCampaignId, setFilterCampaignId] = useState('');

  const campaignNameById = useMemo(() => new Map(_campaigns.map(campaign => [campaign.id, campaign.name])), [_campaigns]);
  const campaignIdByName = useMemo(() => new Map(_campaigns.map(campaign => [campaign.name, campaign.id])), [_campaigns]);
  const sortedCampaigns = useMemo(
    () => [..._campaigns].sort((a, b) => a.name.localeCompare(b.name)),
    [_campaigns]
  );
  const flatItems = useMemo(
    () => masterItems.map(item => mapComputedItemToFlat(item, campaignNameById, campaignIdByName)),
    [masterItems, campaignNameById, campaignIdByName]
  );

  useEffect(() => {
    const withCurrencyLabel = (col: ColumnConfig): ColumnConfig => {
      if (col.key === 'originalPrice') return { ...col, label: `Original Price (${currency})` };
      if (col.key === 'discountAmount') return { ...col, label: `Discount Amount (${currency})` };
      if (col.key === 'newPrice') return { ...col, label: `New Price (${currency})` };
      if (col.key === 'sterlingShare') return { ...col, label: `Sterling Share (${currency})` };
      if (col.key === 'cofundedShare') return { ...col, label: `Co-Funded Share (${currency})` };
      return col;
    };

    setColumns(prev => prev.map(withCurrencyLabel));
    setTempColumns(prev => prev.map(withCurrencyLabel));
  }, [currency]);

  useEffect(() => {
    let cancelled = false;

    const loadMasterData = async () => {
      setIsLoading(true);
      try {
        const token = getToken();
        const response = await fetch('/api/master-data', {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to load master data (${response.status})`);
        }
        const payload = await response.json();
        if (!cancelled) {
          const rows: ComputedMasterItem[] = Array.isArray(payload?.items) ? payload.items : [];
          // Filter out Proposals from Master Data view
          const campaignRows = rows.filter(r => r.submissionType !== 'Proposal');
          setMasterItems(campaignRows);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load master data', err);
          setMasterItems([]);
          setIsLoading(false);
        }
      }
    };

    loadMasterData();
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply filters
  const filteredItems = useMemo(() => {
    return flatItems.filter(item => {
      if (filterCampaignId && item.campaignId !== filterCampaignId) return false;
      if (filterStatus && item.status !== filterStatus) return false;
      if (filterBrand && item.brand !== filterBrand) return false;
      if (filterLifecycle && item.lifecycle !== filterLifecycle) return false;
      if (filterFundingType && item.fundingType !== filterFundingType) return false;
      if (filterDiscountType && item.discountType !== filterDiscountType) return false;
      if (filterHealth && !item.health.includes(filterHealth)) return false;
      if (filterPlatform && !item.platforms.includes(filterPlatform)) return false;
      return true;
    });
  }, [flatItems, filterCampaignId, filterStatus, filterBrand, filterLifecycle, filterFundingType, filterDiscountType, filterHealth, filterPlatform]);

  // Unique values for filters
  const uniqueStatuses = useMemo(() => [...new Set(flatItems.map(i => i.status))].sort(), [flatItems]);
  const uniqueBrands = useMemo(() => [...new Set(flatItems.map(i => i.brand))].sort(), [flatItems]);
  const uniqueLifecycles = useMemo(() => [...new Set(flatItems.map(i => i.lifecycle))].sort(), [flatItems]);
  const uniqueFundingTypes = useMemo(() => [...new Set(flatItems.map(i => i.fundingType).filter(Boolean))].sort(), [flatItems]);
  const uniqueDiscountTypes = useMemo(() => [...new Set(flatItems.map(i => i.discountType).filter(Boolean))].sort(), [flatItems]);
  const uniqueHealthLabels = useMemo(() => [...new Set(flatItems.map(i => {
    const match = i.health.match(/^(\w+)/);
    return match ? match[1] : '';
  }).filter(Boolean))].sort(), [flatItems]);
  const uniquePlatforms = useMemo(() => {
    const all = flatItems.flatMap(i => i.platforms || []);
    return [...new Set(all)].sort();
  }, [flatItems]);
  
  // Calculate totals for current page
  const totals = useMemo(() => {
    const totalOriginal = filteredItems.reduce((sum, item) => sum + parseFloat(item.originalPrice || '0'), 0);
    const totalDiscount = filteredItems.reduce((sum, item) => sum + parseFloat(item.discountAmount || '0'), 0);
    const totalNew = filteredItems.reduce((sum, item) => sum + parseFloat(item.newPrice || '0'), 0);
    
    return {
      originalPrice: totalOriginal.toFixed(2),
      discountAmount: totalDiscount.toFixed(2),
      newPrice: totalNew.toFixed(2),
    };
  }, [filteredItems]);

  // Load saved column config from API
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await apiClient.tablePreferences.get('master-data');
        if (prefs?.columnsConfig && Array.isArray(prefs.columnsConfig)) {
          // Merge saved config with DEFAULT_COLUMNS to preserve labels
          const savedMap = new Map(prefs.columnsConfig.map((c: any) => [c.key, c.visible]));
          const merged = DEFAULT_COLUMNS.map(col => ({
            ...col,
            visible: savedMap.has(col.key) ? savedMap.get(col.key) : col.visible
          }));
          setColumns(merged);
        }
      } catch (err) {
        // Fallback to localStorage
        const saved = localStorage.getItem('sterling_table_columns');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) {
              const savedMap = new Map(parsed.map((c: any) => [c.key, c.visible]));
              const merged = DEFAULT_COLUMNS.map(col => ({
                ...col,
                visible: savedMap.has(col.key) ? savedMap.get(col.key) : col.visible
              }));
              setColumns(merged);
            }
          } catch {}
        }
      }
    };
    loadPreferences();
  }, []);

  const handleOpenColumnModal = () => {
    setTempColumns([...columns]);
    setShowColumnModal(true);
  };

  const handleSaveColumns = async () => {
    setColumns([...tempColumns]);
    localStorage.setItem('sterling_table_columns', JSON.stringify(tempColumns));
    
    try {
      await apiClient.tablePreferences.save('master-data', tempColumns);
    } catch (err) {
      console.error('Failed to save preferences to API:', err);
    }
    
    setShowColumnModal(false);
  };

  const handleCancelColumns = () => {
    setTempColumns([...columns]);
    setShowColumnModal(false);
  };

  const handleResetColumns = () => {
    setTempColumns([...DEFAULT_COLUMNS]);
  };

  const toggleTempColumn = (key: keyof FlatItem) => {
    setTempColumns(prev =>
      prev.map(col =>
        col.key === key ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    
    const items = Array.from(tempColumns);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    
    setTempColumns(items);
  };

  const toggleExportColumn = (key: string) => {
    setSelectedExportColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleExportCurrentView = () => {
    if (!window.ExcelJS) {
      alert('ExcelJS is not loaded. Please check globalLibraries.ts');
      return;
    }

    const formatCell = (value: any) => (Array.isArray(value) ? value.join(', ') : value);

    const workbook = new window.ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Master Data');

    // Header row - use visible columns
    const visibleColumns = columns.filter(c => c.visible);
    sheet.addRow(visibleColumns.map(c => c.label));

    // Data rows - filtered items
    for (const item of filteredItems) {
      sheet.addRow(visibleColumns.map(c => formatCell(item[c.key])));
    }

    workbook.xlsx.writeBuffer().then((buffer: ArrayBuffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `master-data-current-view-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const handleExportFullData = () => {
    if (!window.ExcelJS) {
      alert('ExcelJS is not loaded. Please check globalLibraries.ts');
      return;
    }

    const formatCell = (value: any) => (Array.isArray(value) ? value.join(', ') : value);

    const workbook = new window.ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Master Data');

    // Header row - selected columns from modal
    const exportCols = Array.from(selectedExportColumns);
    const colLabels = exportCols.map(key => {
      const col = DEFAULT_COLUMNS.find(c => c.key === key);
      return col?.label || key;
    });
    sheet.addRow(colLabels);

    // Data rows - all items (not filtered)
    for (const item of flatItems) {
      sheet.addRow(exportCols.map(col => formatCell(item[col as keyof FlatItem])));
    }

    workbook.xlsx.writeBuffer().then((buffer: ArrayBuffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `master-data-full-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
    
    setShowExportModal(false);
  };

  const clearAllFilters = () => {
    setFilterCampaignId('');
    setFilterStatus('');
    setFilterBrand('');
    setFilterLifecycle('');
    setFilterFundingType('');
    setFilterDiscountType('');
    setFilterHealth('');
  };

  const visibleColumns = columns.filter(c => c.visible);
  const hasActiveFilters = filterCampaignId || filterStatus || filterBrand || filterLifecycle || filterFundingType || filterDiscountType || filterHealth;

  if (isLoading && masterItems.length === 0) {
    return <div className="text-gray-500 p-6">Loading Master Data...</div>;
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-zinc-800">Master Data</h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            {filteredItems.length} of {flatItems.length} items
            {hasActiveFilters && <span className="text-teal-600 font-medium"> • Filtered</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleOpenColumnModal}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50 transition-colors"
          >
            <Settings size={16} />
            Columns
          </button>
          <button
            onClick={handleExportCurrentView}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50 transition-colors"
          >
            <Download size={16} />
            Current View
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 transition-colors shadow-sm"
          >
            <Download size={16} />
            Export Full Data
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-md p-3 shadow-sm">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-medium text-zinc-600">
            <Filter size={15} />
            <span>Filters</span>
          </div>

          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 text-xs font-medium border border-zinc-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white text-zinc-700"
          >
            <option value="">All Statuses</option>
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          <select
            value={filterBrand}
            onChange={e => setFilterBrand(e.target.value)}
            className="px-3 py-1.5 text-xs font-medium border border-zinc-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white text-zinc-700"
          >
            <option value="">All Brands</option>
            {uniqueBrands.map(brand => (
              <option key={brand} value={brand}>{brand}</option>
            ))}
          </select>

          <label className="flex items-center gap-2 text-xs font-medium text-zinc-600">
            <span>Campaign</span>
            <select
              value={filterCampaignId}
              onChange={e => setFilterCampaignId(e.target.value)}
              className="px-3 py-1.5 text-xs font-medium border border-zinc-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white text-zinc-700"
            >
              <option value="">All Campaigns</option>
              {sortedCampaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
              ))}
            </select>
          </label>

          <select
            value={filterLifecycle}
            onChange={e => setFilterLifecycle(e.target.value)}
            className="px-3 py-1.5 text-xs font-medium border border-zinc-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white text-zinc-700"
          >
            <option value="">All Lifecycles</option>
            {uniqueLifecycles.map(lifecycle => (
              <option key={lifecycle} value={lifecycle}>{lifecycle}</option>
            ))}
          </select>

          <select
            value={filterFundingType}
            onChange={e => setFilterFundingType(e.target.value)}
            className="px-3 py-1.5 text-xs font-medium border border-zinc-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white text-zinc-700"
          >
            <option value="">All Funding</option>
            {uniqueFundingTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select
            value={filterPlatform}
            onChange={e => setFilterPlatform(e.target.value)}
            className="px-3 py-1.5 text-xs font-medium border border-zinc-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white text-zinc-700"
          >
            <option value="">All Platforms</option>
            {uniquePlatforms.map(platform => (
              <option key={platform} value={platform}>{platform}</option>
            ))}
          </select>

          <select
            value={filterDiscountType}
            onChange={e => setFilterDiscountType(e.target.value)}
            className="px-3 py-1.5 text-xs font-medium border border-zinc-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white text-zinc-700"
          >
            <option value="">All Discount Types</option>
            <option value="Percentage">Percentage</option>
            <option value="Flat">Flat</option>
          </select>

          <select
            value={filterHealth}
            onChange={e => setFilterHealth(e.target.value)}
            className="px-3 py-1.5 text-xs font-medium border border-zinc-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white text-zinc-700"
          >
            <option value="">All Health</option>
            {uniqueHealthLabels.map(label => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="ml-auto px-3 py-1.5 text-xs font-medium text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-md transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        {hasActiveFilters && (
          <div className="mt-2 text-xs text-zinc-500 font-medium">
            {filteredItems.length} rows
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white border border-zinc-200 rounded-md shadow-sm">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gradient-to-r from-zinc-50 to-zinc-100 border-b border-zinc-200">
              {visibleColumns.map(col => (
                <th
                  key={col.key}
                  className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wide whitespace-nowrap"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-4 py-12 text-center">
                  <div className="text-zinc-400">
                    <Filter className="mx-auto mb-2" size={32} />
                    <p className="text-sm font-medium">No items match the current filters</p>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {filteredItems.map((item, idx) => (
                  <tr key={idx} className="hover:bg-zinc-50 transition-colors">
                    {visibleColumns.map(col => {
                      let cellClass = "px-4 py-3 text-xs whitespace-nowrap";
                      let content = item[col.key] || '-';
                      
                      // Style specific columns
                      if (col.key === 'status') {
                        const statusColors: Record<string, string> = {
                          'Approved': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800',
                          'Pending': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800',
                          'Rejected': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800',
                        };
                        return (
                          <td key={col.key} className={cellClass}>
                            <span className={statusColors[content as string] || 'text-zinc-700'}>{content}</span>
                          </td>
                        );
                      } else if (col.key === 'lifecycle') {
                        const lifecycleColors: Record<string, string> = {
                          'Active': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800',
                          'Starting Soon': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-sky-100 text-sky-700',
                          'Ended': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-zinc-200 text-zinc-700',
                        };
                        return (
                          <td key={col.key} className={cellClass}>
                            <span className={lifecycleColors[content as string] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-zinc-100 text-zinc-700'}>
                              {content}
                            </span>
                          </td>
                        );
                      } else if (col.key === 'health') {
                        const isHealthy = content.toString().includes('Healthy');
                        const isAttention = content.toString().includes('Attention');
                        return (
                          <td key={col.key} className={cellClass}>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              isHealthy ? 'bg-green-100 text-green-800' : 
                              isAttention ? 'bg-orange-100 text-orange-800' : 
                              'bg-red-100 text-red-800'
                            }`}>{content}</span>
                          </td>
                        );
                      } else if (col.key === 'fundingType') {
                        const fundingTypeColors: Record<string, string> = {
                          'Co-Funded': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800',
                          'Fully Funded': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800',
                          'None': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600',
                        };
                        return (
                          <td key={col.key} className={cellClass}>
                            <span className={fundingTypeColors[content as string] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600'}>
                              {content}
                            </span>
                          </td>
                        );
                      } else if (col.key === 'discountType') {
                        const discountTypeColors: Record<string, string> = {
                          'Percentage': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-pink-100 text-pink-800',
                          'Flat': 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-800',
                        };
                        return (
                          <td key={col.key} className={cellClass}>
                            <span className={discountTypeColors[content as string] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-zinc-100 text-zinc-600'}>
                              {content}
                            </span>
                          </td>
                        );
                      } else if (col.key === 'originalPrice' || col.key === 'newPrice') {
                        return (
                          <td key={col.key} className={`${cellClass} text-zinc-900 font-medium`}>
                            {currency} {content}
                          </td>
                        );
                      } else if (col.key === 'discountAmount') {
                        return (
                          <td key={col.key} className={`${cellClass} text-red-600 font-medium`}>
                            {currency} {content}
                          </td>
                        );
                      } else if (col.key === 'sterlingShare' || col.key === 'cofundedShare') {
                        return (
                          <td key={col.key} className={`${cellClass} text-zinc-900 font-medium`}>
                            {currency} {content}
                          </td>
                        );
                      } else if (col.key === 'platforms') {
                        const platformList = Array.isArray(item.platforms) ? item.platforms : [];
                        return (
                          <td key={col.key} className={`${cellClass} text-zinc-700`}>
                            {platformList.length === 0 ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-zinc-100 text-zinc-500">
                                None
                              </span>
                            ) : (
                              <div className="flex flex-wrap gap-1.5">
                                {platformList.map((p) => (
                                  <span
                                    key={p}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-teal-50 text-teal-700 border border-teal-100"
                                  >
                                    {p}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        );
                      } else if (col.key === 'discount') {
                        const hasDiscount = content !== '-' && content !== '';
                        return (
                          <td key={col.key} className={cellClass}>
                            <span className={`text-xs font-semibold ${hasDiscount ? 'text-teal-700' : 'text-zinc-400'}`}>
                              {hasDiscount ? content : '-'}
                            </span>
                          </td>
                        );
                      } else if (col.key === 'id') {
                        return (
                          <td key={col.key} className={`${cellClass} text-teal-600 font-medium`}>
                            {content}
                          </td>
                        );
                      } else {
                        return (
                          <td key={col.key} className={`${cellClass} text-zinc-700`}>
                            {content}
                          </td>
                        );
                      }
                    })}
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-gradient-to-r from-zinc-100 to-zinc-50 font-bold border-t-2 border-zinc-300">
                  {visibleColumns.map((col) => {
                    if (col.key === 'id') {
                      return <td key={col.key} className="px-4 py-3 text-xs text-zinc-800 whitespace-nowrap">Total (Page)</td>;
                    } else if (col.key === 'originalPrice') {
                      return <td key={col.key} className="px-4 py-3 text-xs text-zinc-900 whitespace-nowrap">{totals.originalPrice}</td>;
                    } else if (col.key === 'discountAmount') {
                      return <td key={col.key} className="px-4 py-3 text-xs text-red-700 whitespace-nowrap">{totals.discountAmount}</td>;
                    } else if (col.key === 'newPrice') {
                      return <td key={col.key} className="px-4 py-3 text-xs text-zinc-900 whitespace-nowrap">{totals.newPrice}</td>;
                    } else {
                      return <td key={col.key} className="px-4 py-3 text-xs whitespace-nowrap"></td>;
                    }
                  })}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Column Customization Modal with Drag & Drop */}
      {showColumnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-zinc-200">
              <h3 className="text-lg font-bold text-zinc-900">Customize Columns</h3>
              <button
                onClick={handleCancelColumns}
                className="text-zinc-500 hover:text-zinc-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-3 overflow-y-auto max-h-[calc(80vh-200px)]">
              <p className="text-sm text-zinc-600 mb-4">
                Drag to reorder • Toggle visibility • Changes saved per-user
              </p>
              
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="columns">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                      {tempColumns.map((col, index) => (
                        <Draggable key={col.key} draggableId={col.key} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center gap-3 p-3 rounded-lg border ${
                                snapshot.isDragging
                                  ? 'border-teal-500 bg-teal-50 shadow-lg'
                                  : 'border-zinc-200 bg-white hover:bg-zinc-50'
                              }`}
                            >
                              <div {...provided.dragHandleProps} className="text-zinc-400 hover:text-zinc-600 cursor-grab active:cursor-grabbing">
                                <GripVertical size={20} />
                              </div>
                              <input
                                type="checkbox"
                                checked={col.visible}
                                onChange={() => toggleTempColumn(col.key)}
                                className="h-4 w-4 text-teal-600 rounded"
                              />
                              <span className="text-sm font-medium text-zinc-900 flex-1">{col.label}</span>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>

            <div className="flex items-center justify-between p-6 border-t border-zinc-200">
              <button
                onClick={handleResetColumns}
                className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50"
              >
                Reset to Default
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCancelColumns}
                  className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveColumns}
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Full Data Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-zinc-200">
              <h3 className="text-lg font-bold text-zinc-900">Export Full Data</h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-zinc-500 hover:text-zinc-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(80vh-180px)]">
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-4">
                <p className="text-sm text-teal-900">
                  <strong>Export Mode:</strong> Full Data (all {flatItems.length} items, unfiltered)
                </p>
              </div>

              <div>
                <h4 className="text-sm font-bold text-zinc-900 mb-3">Select Columns to Export</h4>
                <div className="grid grid-cols-2 gap-3">
                  {DEFAULT_COLUMNS.map(col => {
                    const isExportable = EXPORT_COLUMNS.includes(col.key);
                    if (!isExportable) return null;
                    
                    return (
                      <label
                        key={col.key}
                        className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedExportColumns.has(col.key)}
                          onChange={() => toggleExportColumn(col.key)}
                          className="h-4 w-4 text-teal-600 rounded"
                        />
                        <span className="text-sm font-medium text-zinc-900">{col.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-zinc-200">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                onClick={handleExportFullData}
                disabled={selectedExportColumns.size === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:bg-zinc-300 disabled:cursor-not-allowed"
              >
                Export {selectedExportColumns.size} Columns
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
